import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Mail, ArrowLeft, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Turnstile from '../components/Turnstile';

type Step = 'credentials' | 'verification';

export default function AdminLogin() {
  const { login, verifyAdminCode } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');

  const handleTurnstileVerify = useCallback((token: string) => setTurnstileToken(token), []);
  const handleTurnstileError = useCallback(() => {
    setError('Verification failed. Please refresh and try again.');
    setTurnstileToken('');
  }, []);
  const handleTurnstileExpire = useCallback(() => setTurnstileToken(''), []);

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!turnstileToken) { setError('Please complete the verification challenge'); return; }
    setLoading(true);
    try {
      const result = await login(email, password, turnstileToken);
      if (result.requiresVerification) {
        // Turnstile tokens are single-use and the previous one was consumed by
        // /api/auth/login. Clear it so the verification step's widget mounts
        // fresh and produces a new token for /admin/verify-code.
        setTurnstileToken('');
        setStep('verification');
      } else if (result.success) {
        navigate('/admin/dashboard');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      setTurnstileToken('');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!turnstileToken) { setError('Please complete the verification challenge'); return; }
    setLoading(true);
    try {
      await verifyAdminCode(email, verificationCode, turnstileToken);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
      // Token was either consumed by the rejected request or is now invalid;
      // wipe it so the widget can issue a new one for the retry.
      setTurnstileToken('');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setVerificationCode('');
    setError('');
    setTurnstileToken('');
  };

  const features = ['Creator PDF analysis', 'Product matching engine', 'GMV estimation', 'PDF report export'];

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#0B0B0B' }}>
      {/* ── Left brand panel — desktop only ─────────────────────── */}
      <div
        className="hidden lg:flex flex-col justify-between w-[400px] shrink-0 p-10 border-r"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <div>
          {/* Wordmark */}
          <Link to="/" className="inline-flex items-baseline gap-0 mb-14">
            <span className="font-display font-bold text-lg text-white" style={{ letterSpacing: '-0.02em' }}>
              CreatorHub
            </span>
            <span className="font-display font-bold text-lg" style={{ color: '#FFD400', letterSpacing: '-0.02em' }}>
              360
            </span>
          </Link>

          {/* Icon + tagline */}
          <div className="mb-8">
            <div
              className="w-12 h-12 flex items-center justify-center mb-6"
              style={{
                backgroundColor: 'rgba(255,212,0,0.10)',
                color: '#FFD400',
                borderRadius: '4px',
                border: '1px solid rgba(255,212,0,0.18)',
              }}
            >
              <ShieldCheck className="w-6 h-6" />
            </div>
            <h2
              className="font-display font-bold text-white leading-tight mb-3"
              style={{ fontSize: '22px', letterSpacing: '-0.02em' }}
            >
              Admin portal.{' '}
              <span style={{ color: '#FFD400' }}>Secure access.</span>
            </h2>
            <p className="text-sm text-white/45 leading-relaxed">
              Analyse creator profiles, match products with AI-powered scoring, and generate revenue estimates.
            </p>
          </div>

          {/* Feature list */}
          <div className="space-y-2.5">
            {features.map((feat) => (
              <div key={feat} className="flex items-center gap-3 text-sm text-white/45">
                <div className="w-1 h-1 shrink-0" style={{ backgroundColor: '#FFD400', borderRadius: '1px' }} />
                {feat}
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-white/20">Private — authorised personnel only</p>
      </div>

      {/* ── Right form panel ─────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[360px]">
          {/* Mobile wordmark */}
          <Link to="/" className="inline-flex items-baseline gap-0 mb-8 lg:hidden">
            <span className="font-display font-bold text-lg text-white" style={{ letterSpacing: '-0.02em' }}>
              CreatorHub
            </span>
            <span className="font-display font-bold text-lg" style={{ color: '#FFD400', letterSpacing: '-0.02em' }}>
              360
            </span>
          </Link>

          <AnimatePresence mode="wait">
            {step === 'credentials' ? (
              <motion.div
                key="credentials"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                <div className="mb-8">
                  <h1
                    className="font-display font-bold text-white mb-1"
                    style={{ fontSize: '24px', letterSpacing: '-0.02em' }}
                  >
                    Sign in
                  </h1>
                  <p className="text-sm text-white/45">Administrative access</p>
                </div>

                <form onSubmit={handleCredentialsSubmit} className="space-y-4">
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="p-3 text-sm text-red-400"
                        style={{
                          backgroundColor: 'rgba(239,68,68,0.08)',
                          border: '1px solid rgba(239,68,68,0.18)',
                          borderRadius: '2px',
                        }}
                      >
                        {error}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="space-y-1.5">
                    <label htmlFor="email" className="block text-xs font-semibold text-white/50 uppercase tracking-wider">
                      Email
                    </label>
                    <input
                      id="email" type="email" value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input" placeholder="admin@example.com"
                      required autoComplete="email"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label htmlFor="password" className="block text-xs font-semibold text-white/50 uppercase tracking-wider">
                      Password
                    </label>
                    <input
                      id="password" type="password" value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input" placeholder="••••••••"
                      required autoComplete="current-password"
                    />
                  </div>

                  <div className="pt-1">
                    <Turnstile
                      onVerify={handleTurnstileVerify}
                      onError={handleTurnstileError}
                      onExpire={handleTurnstileExpire}
                    />
                  </div>

                  <button type="submit" disabled={loading || !turnstileToken} className="btn-primary w-full">
                    {loading
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>
                      : 'Sign In as Admin'
                    }
                  </button>
                </form>
              </motion.div>
            ) : (
              <motion.div
                key="verification"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                <div className="mb-8">
                  <div
                    className="w-10 h-10 flex items-center justify-center mb-5"
                    style={{
                      backgroundColor: 'rgba(74,222,128,0.10)',
                      color: '#4ade80',
                      borderRadius: '2px',
                      border: '1px solid rgba(74,222,128,0.18)',
                    }}
                  >
                    <Mail className="w-5 h-5" />
                  </div>
                  <h1
                    className="font-display font-bold text-white mb-1"
                    style={{ fontSize: '24px', letterSpacing: '-0.02em' }}
                  >
                    Check your email
                  </h1>
                  <p className="text-sm text-white/45">
                    We sent a 6-digit code to{' '}
                    <span className="text-white/80">{email}</span>
                  </p>
                </div>

                <form onSubmit={handleVerificationSubmit} className="space-y-4">
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="p-3 text-sm text-red-400"
                        style={{
                          backgroundColor: 'rgba(239,68,68,0.08)',
                          border: '1px solid rgba(239,68,68,0.18)',
                          borderRadius: '2px',
                        }}
                      >
                        {error}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="space-y-1.5">
                    <label htmlFor="code" className="block text-xs font-semibold text-white/50 uppercase tracking-wider">
                      Verification Code
                    </label>
                    <input
                      id="code" type="text" value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="input text-center text-xl tracking-[0.4em] font-mono"
                      placeholder="000000" maxLength={6} required
                      autoComplete="one-time-code" autoFocus
                    />
                    <p className="text-[11px] text-white/30 text-center">Enter the 6-digit code from your email</p>
                  </div>

                  {/*
                    The /api/auth/admin/verify-code endpoint requires a fresh
                    Turnstile token (the one from /login is single-use and was
                    already consumed). Render a new widget here so the user
                    can complete the second-factor challenge.
                  */}
                  <div className="pt-1">
                    <Turnstile
                      onVerify={handleTurnstileVerify}
                      onError={handleTurnstileError}
                      onExpire={handleTurnstileExpire}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || verificationCode.length !== 6 || !turnstileToken}
                    className="btn-primary w-full"
                  >
                    {loading
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</>
                      : 'Verify & Sign In'
                    }
                  </button>

                  <button
                    type="button"
                    onClick={handleBackToCredentials}
                    className="btn-ghost w-full text-sm flex items-center justify-center gap-1.5"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back to login
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>

          <p className="text-center mt-8 text-xs text-white/25">
            Creator?{' '}
            <Link to="/login" className="text-white/50 hover:text-white transition-colors">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
